import { useCallback, useEffect, useMemo } from "react"

import {
  useTransportInfiniteQuery,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import { getDefaultTagColor } from "../constants/tag-colors"
import type { OrganizationTag, TagSuggestion } from "../types"
import {
  buildOrganizationTagsByName,
  buildTagColorMap,
} from "../utils/tagCatalog"

const LIST_ORGANIZATION_TAGS_OPERATION = "listOrganizationTags"
const LIST_WORKSPACE_TAGS_OPERATION = "listWorkspaceTags"

/**
 * `listOrganizationTags` returns at most this many tags, ordered by name, and
 * no page token. A response this long may be truncated.
 */
export const ORGANIZATION_TAGS_PICKER_LIMIT = 200

interface UseOrganizationTagsOptions {
  enabled?: boolean
}

interface UseOrganizationTagsResult {
  tags: OrganizationTag[]
  suggestions: TagSuggestion[]
  tagColorMap: Record<string, string | null>
  tagsByName: Record<string, OrganizationTag>
  isLoading: boolean
  isError: boolean
  error: Error | null
  invalidate: () => Promise<void>
}

interface RawTag {
  id: string
  name: string
  color?: string | null
}

function toOrganizationTags(rawTags: readonly RawTag[]): OrganizationTag[] {
  const seen = new Set<string>()
  const tags: OrganizationTag[] = []
  for (const tag of rawTags) {
    if (seen.has(tag.id)) {
      continue
    }
    seen.add(tag.id)
    tags.push({
      id: tag.id,
      name: tag.name,
      color: tag.color ?? getDefaultTagColor(tag.name),
    })
  }
  return tags
}

/**
 * The organization's active tags for pickers, filters, and color lookups.
 *
 * Reads the replica-backed `listOrganizationTags` first. That endpoint stops at
 * 200 tags, so when it comes back full, the hook pages through the
 * `listWorkspaceTags` catalog to load the rest. Without this, tags that sort
 * after the 200th name could not be found, filtered on, or colored.
 */
export function useOrganizationTags(
  options?: UseOrganizationTagsOptions,
): UseOrganizationTagsResult {
  const transportQueryUtils = useTransportQueryUtils()
  const enabled = options?.enabled ?? true
  const query = useTransportQuery({
    operation: LIST_ORGANIZATION_TAGS_OPERATION,
    options: { enabled },
  })

  const isTruncated =
    (query.data?.tags.length ?? 0) >= ORGANIZATION_TAGS_PICKER_LIMIT
  const catalogQuery = useTransportInfiniteQuery({
    operation: LIST_WORKSPACE_TAGS_OPERATION,
    request: { page_size: ORGANIZATION_TAGS_PICKER_LIMIT },
    getNextRequest: (lastPage, _pages, previousRequest) =>
      lastPage.next_page_token && previousRequest
        ? { ...previousRequest, page_token: lastPage.next_page_token }
        : null,
    options: { enabled: enabled && isTruncated },
  })
  const { fetchNextPage, hasNextPage, isFetching } = catalogQuery
  const isCatalogError = isTruncated && catalogQuery.isError

  useEffect(() => {
    if (isTruncated && hasNextPage && !isFetching && !isCatalogError) {
      // Every tag cell in a table mounts this hook. `cancelRefetch: false`
      // makes the other instances' calls no-ops while one page is in flight,
      // instead of each restarting the same request.
      void fetchNextPage({ cancelRefetch: false })
    }
  }, [fetchNextPage, hasNextPage, isCatalogError, isFetching, isTruncated])

  const tags = useMemo<OrganizationTag[]>(() => {
    const firstPage = query.data?.tags ?? []
    const catalogTags = isTruncated
      ? (catalogQuery.data?.pages ?? []).flatMap((page) => page?.tags ?? [])
      : []
    // Until the catalog arrives, keep showing the first 200; the catalog then
    // adds the rest without dropping anything already visible.
    return toOrganizationTags([...firstPage, ...catalogTags])
  }, [catalogQuery.data, isTruncated, query.data])

  const suggestions = useMemo<TagSuggestion[]>(() => {
    return tags.map((tag) => ({
      id: tag.id,
      tag: tag.name,
      color: tag.color ?? getDefaultTagColor(tag.name),
    }))
  }, [tags])

  const tagColorMap = useMemo(
    () => buildTagColorMap(suggestions),
    [suggestions],
  )
  const tagsByName = useMemo(() => buildOrganizationTagsByName(tags), [tags])

  const invalidate = useCallback(async () => {
    await Promise.all([
      transportQueryUtils.invalidate(LIST_ORGANIZATION_TAGS_OPERATION),
      transportQueryUtils.invalidate(LIST_WORKSPACE_TAGS_OPERATION, {
        page_size: ORGANIZATION_TAGS_PICKER_LIMIT,
      }),
    ])
  }, [transportQueryUtils])

  return {
    tags,
    suggestions,
    tagColorMap,
    tagsByName,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    invalidate,
  }
}
